import { useTranslation } from 'react-i18next';
import { WeekFilter } from '../ui/week-filter';
import { matchesWeek } from '../../lib/weekFilter';
import i18n from '../../i18n';
import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Plus, Search, Calendar, Users, Filter, Trash2, 
  Clock, CheckCircle, AlertCircle, FileText, Link2, FolderOpen,
  ClipboardList, FileSignature
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import EntityLinker, { EntityLinkBadge } from './EntityLinker';
import AuditReport from './AuditReport';
import ElectronicSignaturePanel from './ElectronicSignature';
import usePermissions from '../../hooks/usePermissions';
import { DateText } from '../ui/week-badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const AUDIT_TYPES = [
  { value: 'internal', label: i18n.t('auto.lbl.internalAudit') },
  { value: 'external', label: i18n.t('auto.lbl.externalAudit') },
  { value: 'supplier', label: i18n.t('auto.lbl.supplierAudit') },
  { value: 'certification', label: i18n.t('auto.lbl.certificationAudit') },
  { value: 'surveillance', label: i18n.t('auto.lbl.surveillanceAudit') },
];

const AUDIT_STATUS = [
  { value: 'planned', label: i18n.t('auto.lbl.planned'), color: 'bg-blue-100 text-blue-800' },
  { value: 'in_progress', label: i18n.t('auto.lbl.inProgress'), color: 'bg-yellow-100 text-yellow-800' },
  { value: 'completed', label: i18n.t('auto.lbl.completed'), color: 'bg-green-100 text-green-800' },
  { value: 'cancelled', label: i18n.t('auto.lbl.cancelled'), color: 'bg-gray-100 text-gray-800' },
];

const AuditManagement = ({ audits, employees, projects, documents, onRefresh, token, navContext }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportAudit, setReportAudit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Auto-open the linked audit report when navigated from inventory
  React.useEffect(() => {
    if (navContext?.auditId && audits.length > 0) {
      const audit = audits.find(a => a.id === navContext.auditId);
      if (audit) {
        setReportAudit(audit);
        setShowReportDialog(true);
      }
    }
  }, [navContext, audits]);

  const [formData, setFormData] = useState({
    audit_number: '',
    title: '',
    audit_type: 'internal',
    url: '',
    scope: '',
    objectives: '',
    criteria: '',
    planned_date: format(new Date(), 'yyyy-MM-dd'),
    duration_days: 1,
    lead_auditor_id: '',
    auditor_ids: [],
    auditee_ids: [],
    linked_project_ids: [],
    linked_document_ids: [],
  });

  const filteredAudits = audits.filter(audit => {
    const matchesSearch = audit.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          audit.audit_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || audit.status === filterStatus;
    const matchesType = filterType === 'all' || audit.audit_type === filterType;
    return matchesSearch && matchesStatus && matchesType && matchesWeek(weekFilter, audit.planned_date);
  });

  const handleCreate = async () => {
    if (!formData.audit_number || !formData.title || !formData.lead_auditor_id) {
      toast.error(t('auto.iso.auditNumberTitleAndLeadAuditor'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/iso/audits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          planned_date: new Date(formData.planned_date).toISOString(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create audit');
      }

      toast.success(t('auto.iso.auditCreatedSuccessfully'));
      setShowCreateDialog(false);
      setFormData({
        audit_number: '',
        title: '',
        audit_type: 'internal',
        url: '',
        scope: '',
        objectives: '',
        criteria: '',
        planned_date: format(new Date(), 'yyyy-MM-dd'),
        duration_days: 1,
        lead_auditor_id: '',
        auditor_ids: [],
        auditee_ids: [],
        linked_project_ids: [],
        linked_document_ids: [],
      });
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (auditId, newStatus) => {
    try {
      const body = { status: newStatus };
      if (newStatus === 'completed') {
        body.actual_date = new Date().toISOString();
      }

      const response = await fetch(`${API_BASE}/api/iso/audits/${auditId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to update status');
      toast.success(t('auto.iso.auditStatusUpdated'));
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (auditId) => {
    if (!window.confirm(t('auto.iso.areYouSureYouWantTo'))) return;

    try {
      const response = await fetch(`${API_BASE}/api/iso/audits/${auditId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete audit');
      toast.success(t('auto.iso.auditDeleted'));
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp?.name || 'Unknown';
  };

  const getStatusBadge = (status) => {
    const statusInfo = AUDIT_STATUS.find(s => s.value === status);
    return <Badge className={statusInfo?.color}>{statusInfo?.label || status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const typeInfo = AUDIT_TYPES.find(t => t.value === type);
    return <Badge variant="outline">{typeInfo?.label || type}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="audit-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="relative min-w-[180px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('auto.iso.searchAudits')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="audits-week-filter" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px] max-w-full">
              <SelectValue placeholder={t('auto.iso.type')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allTypes')}</SelectItem>
              {AUDIT_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] max-w-full">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t('auto.iso.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allStatus')}</SelectItem>
              {AUDIT_STATUS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {can('iso_audits.create') && (
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-audit-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('auto.iso.newAudit')}
          </Button>
        )}
      </div>

      {/* Audits List */}
      <div className="grid gap-4">
        {filteredAudits.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>{t('auto.iso.noAuditsFound')}</p>
            </CardContent>
          </Card>
        ) : (
          filteredAudits.map(audit => (
            <Card key={audit.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Calendar className="h-4 w-4 text-purple-600" />
                      <span className="font-mono text-sm text-gray-500">{audit.audit_number}</span>
                      {getTypeBadge(audit.audit_type)}
                      {getStatusBadge(audit.status)}
                    </div>
                    <h3 className="font-medium">{audit.title}</h3>
                    {audit.scope && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{audit.scope}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {<DateText value={audit.planned_date} />}
                        {audit.duration_days > 1 && ` (${audit.duration_days} days)`}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        Lead: {getEmployeeName(audit.lead_auditor_id)}
                      </span>
                      {audit.findings?.length > 0 && (
                        <span className="flex items-center gap-1 text-orange-500">
                          <AlertCircle className="h-3 w-3" />
                          {audit.findings.length} finding(s)
                        </span>
                      )}
                    </div>
                    {/* Linked projects */}
                    {audit.linked_project_ids?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {audit.linked_project_ids?.slice(0, 3).map(id => {
                          const proj = projects.find(p => p.id === id);
                          return proj ? (
                            <EntityLinkBadge key={id} type="project" name={proj.name} />
                          ) : null;
                        })}
                        {audit.linked_project_ids?.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{audit.linked_project_ids.length - 3} more</Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    <Select
                      value={audit.status}
                      onValueChange={(value) => handleUpdateStatus(audit.id, value)}
                    >
                      <SelectTrigger className="h-8 w-[130px] max-w-full text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AUDIT_STATUS.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setReportAudit(audit);
                        setShowReportDialog(true);
                      }}
                      data-testid={`audit-report-btn-${audit.id}`}
                    >
                      <ClipboardList className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleDelete(audit.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Audit Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>{t('auto.iso.auditReport')}</DialogTitle>
            <DialogDescription>{t('auto.iso.viewAndEditTheAuditReport')}</DialogDescription>
          </DialogHeader>
          {reportAudit && (
            <AuditReport 
              audit={reportAudit} 
              token={token}
              onClose={() => setShowReportDialog(false)}
              onSave={() => onRefresh?.()}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Audit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('auto.iso.scheduleNewAudit')}</DialogTitle>
            <DialogDescription className="sr-only">{t('auto.iso.createANewIsoAudit')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.auditNumber')}</label>
                <Input
                  value={formData.audit_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, audit_number: e.target.value  }))}
                  placeholder="e.g., AUD-2024-001"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.auditType')}</label>
                <Select
                  value={formData.audit_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, audit_type: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.title')}</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value  }))}
                placeholder={t('auto.iso.auditTitle')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.externalUrl')}</label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value  }))}
                placeholder="https://example.com/audit-portal"
                data-testid="audit-url-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.scope')}</label>
              <textarea
                className="w-full min-h-[60px] p-2 border rounded-md text-sm"
                value={formData.scope}
                onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value  }))}
                placeholder={t('auto.iso.auditScope')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.objectives')}</label>
              <textarea
                className="w-full min-h-[60px] p-2 border rounded-md text-sm"
                value={formData.objectives}
                onChange={(e) => setFormData(prev => ({ ...prev, objectives: e.target.value  }))}
                placeholder={t('auto.iso.auditObjectives')}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.plannedDate')}</label>
                <Input
                  type="date"
                  value={formData.planned_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, planned_date: e.target.value  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.durationDays')}</label>
                <Input
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_days: parseInt(e.target.value) || 1  }))}
                  min="1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.leadAuditor')}</label>
              <Select
                value={formData.lead_auditor_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, lead_auditor_id: value  }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('auto.iso.selectLeadAuditor')} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.auditCriteria')}</label>
              <Input
                value={formData.criteria}
                onChange={(e) => setFormData(prev => ({ ...prev, criteria: e.target.value  }))}
                placeholder="e.g., ISO 9001:2015, Company Procedures"
              />
            </div>
            
            {/* Entity Linking */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                {t('auto.iso.linkToProjects')}
              </h4>
              <EntityLinker
                linkedProjectIds={formData.linked_project_ids}
                linkedEmployeeIds={[]}
                projects={projects}
                employees={employees}
                onProjectsChange={(ids) => setFormData(prev => ({ ...prev, linked_project_ids: ids  }))}
                onEmployeesChange={() => {}}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={handleCreate} disabled={isLoading} data-testid="dialog-create-btn">
              {isLoading ? t('auto.iso.creating') : t('auto.iso.scheduleAudit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditManagement;
